import GenericAssetView from "../views/generic-asset-view";
import Marker3DView from "../views/marker-3d-view";
import WallView, {EXTENSION_LINE_MATERIAL} from "../views/wall-view";
import createTechDrawingLabel from "./create-tech-drawing-label";
import logging from '../logging';
import traverse from "./traverse-generator";
import { Object3D, LineBasicMaterial, MeshBasicMaterial, Mesh, BufferGeometry, EdgesGeometry, Material, LineSegments, Vector3, WireframeGeometry } from "three";
import { getParentViewOrNull } from "../controllers/base-view-controllers";

const log = logging('utils');

const isMesh = (obj: Object3D): obj is Mesh => {
    return Boolean(obj["isMesh"]);
}

/**
 * In an earlier version, the AssemblyBuilder handled creating the 3D objects
 * with the technical document materials. This does the same thing so that
 * the old code may run unmodified with the new domain model and view classes.
 */
const toTechnicalDocument = (root: Object3D, isMetric: boolean, doorOpacity: number = 1) => {

    const
        lineMaterial = new LineBasicMaterial(
            { color: 0x000000, linewidth: 2 }),
        backingMaterial = new LineBasicMaterial(
            { color: 0x000000, depthTest: false, opacity: 0.25, transparent: true }),
        background = new MeshBasicMaterial(
            { color: 0xFFFFFF, opacity: 1, transparent: false }),
        transparent = new MeshBasicMaterial(
            { color: 0x000000, opacity: 0.25, transparent: true }),
        doorMaterial = new MeshBasicMaterial(
            { color: 0xFFFFFF, opacity: doorOpacity, transparent: doorOpacity != 1 }),
        lightMaterial = new MeshBasicMaterial({ color: 0x2C76CF }),
        lights: Mesh[] = [],
        meshes: Mesh[] = [],
        cache = new Map<BufferGeometry, EdgesGeometry>(),
        wireframes: Mesh[] = [],
        wirecache = new Map<BufferGeometry, WireframeGeometry>();

    for(const obj of traverse(root)) {
        if(!obj.visible) continue;
        if(obj.userData.isConvertedForTechDoc) continue;
        if(isMesh(obj)) {

            if(obj.userData.isTechLabelMesh) continue;

            if(obj.userData.isLightMesh) {
                lights.push(obj);
            } else if(obj.userData.isBackingMesh) {
                wireframes.push(obj);
            } else {
                meshes.push(obj);
            }
        }
    }
    for (const mesh of lights) {


        const view = getParentViewOrNull(mesh) as GenericAssetView;
        if(view) {
            if(view.model.filename) {
                if(view.model.filename.includes('strip_light')) {
                    if(view.scale.x == 1) view.scale.x = 12;
                    if(view.scale.y == 1) view.scale.y = 12;
                } else if(view.model.filename.includes('puck_light')) {
                    view.scale.y = 2;
                }
            }
            if(Array.isArray(mesh.material)) {
                const mats = new Array(mesh.material.length);
                mats.fill(lightMaterial);
                mesh.material = mats;
            } else {
                mesh.material = lightMaterial;
            }
        }
    }
    for (const mesh of meshes) {
        try {
            if(mesh.material == EXTENSION_LINE_MATERIAL) {
                mesh.removeFromParent();
                continue;
            }
            let material: Material;
            if (mesh.userData.isDoorMesh) {
                mesh.material = doorMaterial;
            } else {
                if (Array.isArray(mesh.material)) {
                    material = mesh.material[0];
                } else {
                    material = mesh.material;
                }
                switch (material.name) {
                    case 'Glass':
                    case 'Mirror':
                    case 'FrostedGlass':
                        mesh.material = transparent;
                        break;
                    default:
                        mesh.material = background;
                }
            }

            let edges: EdgesGeometry;
            if (mesh.geometry == null) {
                continue;
            }
            else if (cache.has(mesh.geometry)) {
                edges = cache.get(mesh.geometry);
            } else {
                const pos = mesh.geometry.getAttribute('position');
                if (pos && pos.count > 0) {
                    edges = new EdgesGeometry(mesh.geometry);
                    cache.set(mesh.geometry, edges);
                }
            }

            const lines = new LineSegments(edges, lineMaterial);

            lines.position.copy(mesh.position);
            lines.rotation.copy(mesh.rotation);
            lines.scale.copy(mesh.scale);
            mesh.parent.add(lines);
            mesh.userData.isConvertedForTechDoc = true;
        } catch (error) {
            log.error(error);
        }
    }
    for(const mesh of wireframes) {
        let wire: WireframeGeometry;
        if(wirecache.has(mesh.geometry)) {
            wire = wirecache.get(mesh.geometry);
        }
        else {
            wire = new WireframeGeometry(mesh.geometry);
            wirecache.set(mesh.geometry, wire);
        }

        const lines = new LineSegments(wire, backingMaterial);
        lines.position.copy(mesh.position);
        lines.rotation.copy(mesh.rotation);
        lines.scale.copy(mesh.scale);
        mesh.parent.add(lines);
        mesh.visible = false;
        mesh.userData.isConvertedForTechDoc = true;
    }

    createWallHeightLabels(root, isMetric);
    createMarker3DLabels(root);
};

const createWallHeightLabels = (root: Object3D, isMetric: boolean) => {
    root.traverse(obj => {
        if(obj instanceof WallView && !obj.model.island) {
            const wall = obj.model;

            for(const point of wall.heights) {
                const
                    height = Math.round(point.height * 1000) / 1000,
                    offset = wall.leftExtension ?? 0;
                createTechDrawingLabel(
                    String(isMetric ? Math.round(25.4*height) : height),
                    2.8, // inches
                    point.x + offset, // inches
                    point.height, // inches
                    root,
                );
            }
        }
    });
};

const createMarker3DLabels = (root: Object3D) => {
    const v = new Vector3();
    root.traverse(obj => {
        if (obj instanceof Marker3DView && obj.model.type == "tech-drawing-label") {
            obj.getWorldPosition(v);
            createTechDrawingLabel(
                obj.model.label,
                2.0, // inches
                v.x, v.y,
                root);
        }
    });
};

export default toTechnicalDocument;
